import { createElement, type CSSProperties, type ReactElement } from 'react';

export type SiteMarketingSettings = {
  tag_google_head?: string | null;
  tag_google_body?: string | null;
  google_analytics_head?: string | null;
  google_analytics_body?: string | null;
};

export type MarketingSnippetTarget = 'head' | 'body';

export const MARKETING_SNIPPET_MARKERS = [
  'tag_google_head',
  'google_analytics_head',
  'tag_google_body',
  'google_analytics_body',
] as const;

export type MarketingSnippetMarker = (typeof MARKETING_SNIPPET_MARKERS)[number];

type AllowedSnippetTag = 'script' | 'noscript' | 'link' | 'meta' | 'style' | 'iframe';

type ParsedSnippetPart = {
  tag: AllowedSnippetTag;
  attributes: Record<string, string | boolean>;
  innerHtml: string;
};

export type PreparedMarketingSnippet = {
  marker: MarketingSnippetMarker;
  target: MarketingSnippetTarget;
  html: string;
  parts: ParsedSnippetPart[];
};

export type InvalidMarketingSnippet = {
  marker: MarketingSnippetMarker;
  errors: string[];
};

export type PreparedMarketingSnippets = {
  valid: PreparedMarketingSnippet[];
  invalid: InvalidMarketingSnippet[];
};

type RawSnippetDefinition = {
  marker: MarketingSnippetMarker;
  target: MarketingSnippetTarget;
  html: string | null | undefined;
  expectScript: boolean;
};

const ALLOWED_TAG_PATTERN =
  /<!--[\s\S]*?-->|<(script|noscript|style|iframe)\b([^>]*)>([\s\S]*?)<\/\1\s*>|<(link|meta)\b([^>]*)\/?>/gi;
const ATTRIBUTE_PATTERN =
  /([^\s=/>"'`]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const IFRAME_PATTERN = /<iframe\b([^>]*)>([\s\S]*?)<\/iframe\s*>|<iframe\b([^>]*)\/?>/gi;

const BOOLEAN_ATTRIBUTES = new Set([
  'async',
  'defer',
  'disabled',
  'hidden',
  'nomodule',
]);

const REACT_ATTRIBUTE_NAME_MAP: Record<string, string> = {
  allowfullscreen: 'allowFullScreen',
  charset: 'charSet',
  class: 'className',
  crossorigin: 'crossOrigin',
  enctype: 'encType',
  for: 'htmlFor',
  frameborder: 'frameBorder',
  httpequiv: 'httpEquiv',
  'http-equiv': 'httpEquiv',
  referrerpolicy: 'referrerPolicy',
  tabindex: 'tabIndex',
};

export const ALLOW_MARKETING_SRC_PREFIX = [
  'https://www.googletagmanager.com',
  'https://www.google-analytics.com',
  'https://connect.facebook.net',
  'https://imob.sobressai.com.br',
  'https://novaapi.sobressai.com.br',
  'https://d335luupugsy2.cloudfront.net',
];

function stripIgnorableHtml(source: string): string {
  return source.replace(HTML_COMMENT_PATTERN, '').trim();
}

function parseHtmlAttributes(source: string): Record<string, string | boolean> {
  const attributes: Record<string, string | boolean> = {};

  ATTRIBUTE_PATTERN.lastIndex = 0;
  let match = ATTRIBUTE_PATTERN.exec(source);

  while (match) {
    const rawName = String(match[1] || '').trim();
    if (rawName) {
      const name = rawName.toLowerCase();
      const rawValue = match[2] ?? match[3] ?? match[4];
      attributes[name] =
        rawValue === undefined ? BOOLEAN_ATTRIBUTES.has(name) || true : String(rawValue);
    }
    match = ATTRIBUTE_PATTERN.exec(source);
  }

  return attributes;
}

function validateAllowedSrc(
  src: unknown,
  errors: string[],
  errorPrefix: string
): void {
  const value = String(src || '').trim();
  if (!value) return;

  if (!/^https:\/\//i.test(value)) {
    errors.push(`${errorPrefix} src invalido (exige https): ${value}`);
    return;
  }

  if (!ALLOW_MARKETING_SRC_PREFIX.some((prefix) => value.startsWith(prefix))) {
    errors.push(`${errorPrefix} dominio nao permitido: ${value}`);
  }
}

function validateNoscriptIframes(innerHtml: string, errors: string[]): void {
  IFRAME_PATTERN.lastIndex = 0;
  let match = IFRAME_PATTERN.exec(innerHtml);

  while (match) {
    const iframeAttributes = parseHtmlAttributes(match[1] || match[3] || '');
    validateAllowedSrc(
      typeof iframeAttributes.src === 'string' ? iframeAttributes.src : '',
      errors,
      '<noscript><iframe>'
    );
    match = IFRAME_PATTERN.exec(innerHtml);
  }
}

function tokenizeSnippet(
  html: string
): { parts: ParsedSnippetPart[]; errors: string[] } {
  const parts: ParsedSnippetPart[] = [];
  const errors: string[] = [];

  ALLOWED_TAG_PATTERN.lastIndex = 0;
  let lastIndex = 0;
  let match = ALLOWED_TAG_PATTERN.exec(html);

  while (match) {
    const unexpected = stripIgnorableHtml(html.slice(lastIndex, match.index));
    if (unexpected) {
      errors.push(`conteudo inesperado fora das tags permitidas: ${unexpected.slice(0, 80)}`);
    }

    lastIndex = ALLOWED_TAG_PATTERN.lastIndex;

    if (!match[0].startsWith('<!--')) {
      const tag = (match[1] || match[4] || '').toLowerCase() as AllowedSnippetTag;
      const attributeSource = match[2] || match[5] || '';
      const innerHtml = match[3] || '';

      parts.push({
        tag,
        attributes: parseHtmlAttributes(attributeSource),
        innerHtml,
      });
    }

    match = ALLOWED_TAG_PATTERN.exec(html);
  }

  const remaining = stripIgnorableHtml(html.slice(lastIndex));
  if (remaining) {
    errors.push(`conteudo inesperado fora das tags permitidas: ${remaining.slice(0, 80)}`);
  }

  return { parts, errors };
}

function toReactStyle(styleSource: string): CSSProperties {
  return styleSource
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .reduce<CSSProperties>((style, chunk) => {
      const separatorIndex = chunk.indexOf(':');
      if (separatorIndex < 0) return style;

      const property = chunk.slice(0, separatorIndex).trim().replace(/-([a-z])/g, (_, char) =>
        String(char).toUpperCase()
      );
      const value = chunk.slice(separatorIndex + 1).trim();

      if (!property || !value) return style;

      return {
        ...style,
        [property]: value,
      };
    }, {});
}

function toReactAttributeName(name: string): string {
  if (name.startsWith('data-') || name.startsWith('aria-')) return name;
  return REACT_ATTRIBUTE_NAME_MAP[name] || name;
}

function toReactProps(attributes: Record<string, string | boolean>): Record<string, unknown> {
  return Object.entries(attributes).reduce<Record<string, unknown>>((props, [name, value]) => {
    const propName = toReactAttributeName(name);
    if (propName === 'style' && typeof value === 'string') {
      props.style = toReactStyle(value);
      return props;
    }

    props[propName] = value;
    return props;
  }, {});
}

function sanitizeSnippet(
  definition: RawSnippetDefinition
): { snippet?: PreparedMarketingSnippet; errors: string[] } {
  if (!definition.html || typeof definition.html !== 'string') {
    return { errors: ['snippet vazio'] };
  }

  const html = definition.html.trim();
  if (!html) {
    return { errors: ['snippet vazio'] };
  }

  const { parts, errors } = tokenizeSnippet(html);
  const scripts = parts.filter((part) => part.tag === 'script');

  if (definition.expectScript && scripts.length === 0) {
    errors.push('nenhuma <script> encontrada');
  }

  for (const part of parts) {
    if (part.tag === 'script') {
      const type = String(part.attributes.type || '').toLowerCase();
      const src = typeof part.attributes.src === 'string' ? part.attributes.src : '';

      if (
        type &&
        type !== 'text/javascript' &&
        type !== 'application/javascript' &&
        type !== 'module' &&
        type !== 'application/ld+json'
      ) {
        errors.push(`type inesperado no <script>: ${type}`);
        continue;
      }

      if (src) {
        validateAllowedSrc(src, errors, '<script>');
        continue;
      }

      const code = part.innerHtml.trim();
      if (!code && type !== 'application/ld+json') {
        errors.push('script inline vazio');
        continue;
      }

      if (code && type !== 'application/ld+json') {
        try {
          // Validate syntax without executing the snippet.
          // eslint-disable-next-line no-new-func
          new Function(code);
        } catch (error: any) {
          errors.push(`JS inline com erro de sintaxe: ${String(error?.message || error)}`);
        }
      }
      continue;
    }

    if (part.tag === 'noscript') {
      validateNoscriptIframes(part.innerHtml, errors);
      continue;
    }

    if (part.tag === 'iframe') {
      validateAllowedSrc(
        typeof part.attributes.src === 'string' ? part.attributes.src : '',
        errors,
        '<iframe>'
      );
    }
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    snippet: {
      marker: definition.marker,
      target: definition.target,
      html,
      parts,
    },
    errors: [],
  };
}

function getSnippetDefinitions(site?: SiteMarketingSettings | null): RawSnippetDefinition[] {
  const safeSite = site || {};
  const hasAnyGtm = hasGtmSnippet(safeSite.tag_google_head) || hasGtmSnippet(safeSite.tag_google_body);

  return [
    {
      marker: 'tag_google_head',
      target: 'head',
      html: safeSite.tag_google_head,
      expectScript: true,
    },
    {
      marker: 'google_analytics_head',
      target: 'head',
      html: hasAnyGtm ? null : safeSite.google_analytics_head,
      expectScript: true,
    },
    {
      marker: 'tag_google_body',
      target: 'body',
      html: safeSite.tag_google_body,
      expectScript: false,
    },
    {
      marker: 'google_analytics_body',
      target: 'body',
      html: hasAnyGtm ? null : safeSite.google_analytics_body,
      expectScript: false,
    },
  ];
}

export function hasGtmSnippet(html: string | null | undefined): boolean {
  if (!html) return false;
  return /googletagmanager\.com\/gtm\.js|GTM-[A-Z0-9]+|dataLayer/i.test(html);
}

export function prepareMarketingSnippets(
  site?: SiteMarketingSettings | null,
  enabled = false
): PreparedMarketingSnippets {
  if (!enabled || !site) {
    return { valid: [], invalid: [] };
  }

  return getSnippetDefinitions(site).reduce<PreparedMarketingSnippets>(
    (result, definition) => {
      if (!definition.html) return result;

      const sanitized = sanitizeSnippet(definition);
      if (sanitized.snippet) {
        result.valid.push(sanitized.snippet);
        return result;
      }

      result.invalid.push({
        marker: definition.marker,
        errors: sanitized.errors,
      });
      return result;
    },
    { valid: [], invalid: [] }
  );
}

export function renderMarketingSnippetElements(
  snippet: PreparedMarketingSnippet,
  usePartytown = false
): ReactElement[] {
  return snippet.parts.map((part, index) => {
    const props: Record<string, unknown> = {
      key: `${snippet.marker}-${index}`,
      'data-injected': snippet.marker,
    };

    Object.assign(props, toReactProps(part.attributes));

    if (part.tag === 'script' || part.tag === 'noscript' || part.tag === 'style') {
      // Partytown: roda o <script> (GTM) no web worker -> tira o parse/exec do GTM
      // da main-thread (TBT). type=text/partytown faz o loader interceptar; o
      // forward (dataLayer.push/gtag) garante que os eventos do main-thread cheguem.
      if (usePartytown && part.tag === 'script') {
        props.type = 'text/partytown';
      }
      if (part.innerHtml) {
        props.dangerouslySetInnerHTML = { __html: part.innerHtml };
      }

      return createElement(part.tag, props);
    }

    return createElement(part.tag, props);
  });
}
